import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React from 'react'
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import './App.css'
import { AuthProvider, RequireRole, useAuth } from './auth/AuthContext'
import { Roles } from './auth/rbac'
import { useNotifications } from './ui/NotificationCenter'
import AvailabilityAdminPage from './pages/AvailabilityAdmin'
import CounterpartDirectoryPage from './pages/CounterpartDirectory'
import DashboardPage from './pages/Dashboard'
import FilesPage from './pages/Files'
import AuditLogsPage from './pages/AuditLogs'
import ForbiddenPage from './pages/Forbidden'
// 초대(Invites) 기능은 오픈베타에서는 비활성화한다. (필요 시 추후 재도입)
import LoginPage from './pages/Login'
import SignupPage from './pages/Signup'
import TermsPage from './pages/Terms'
import PrivacyPage from './pages/Privacy'
import NotFoundPage from './pages/NotFound'
import NoticeDetailPage from './pages/NoticeDetail'
import NoticeEditPage from './pages/NoticeEdit'
import NoticeLogsPage from './pages/NoticeLogs'
import NoticeNewPage from './pages/NoticeNew'
import NoticesPage from './pages/Notices'
import NoticeTemplatesPage from './pages/NoticeTemplates'
import OrgsPage from './pages/Orgs'
import ProfilePage from './pages/Profile'
import ReportsPage from './pages/Reports'
import HealthAnalysisPage from './pages/HealthAnalysis'
import CustomersPage from './pages/Customers'
import SupportInquiriesPage from './pages/SupportInquiries'
import SupportInquiryDetailPage from './pages/SupportInquiryDetail'
import SalesImagesListPage from './pages/SalesImagesList'
import SalesImagesCreatePage from './pages/SalesImagesCreate'
import SalesImagesEditPage from './pages/SalesImagesEdit'
import FireBuildingRegistryPage from './pages/FireBuildingRegistry'
import RequestDetailPage from './pages/RequestDetail'
import RequestNewPage from './pages/RequestNew'
import RequestSharePage from './pages/RequestShare'
import RequestsPage from './pages/Requests'
import SubscriptionsPage from './pages/Subscriptions'
import Uw3nnExceptionRulesAdminPage from './pages/Uw3nnExceptionRulesAdmin'
import UserDetailPage from './pages/UserDetail'
import UsersPage from './pages/Users'
import SignupRequestsPage from './pages/SignupRequests'
import SignupRequestDetailPage from './pages/SignupRequestDetail'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { NotificationProvider } from './ui/NotificationCenter'
import { ToastProvider } from './ui/ToastProvider'

// 역할 그룹(모듈 스코프에서 정의: 라우트 가드와 Shell 모두 사용)
// NOTE: "admin 계열" 역할은 메뉴/라우트 권한에서 리더급으로 취급(관리 화면 접근 허용)
const GA_ADMIN_ROLES = [Roles.GA_MASTER_ADMIN, Roles.GA_MASTER_DEPUTY_ADMIN, Roles.GA_OTHER_ADMIN]
const INS_ADMIN_ROLES = [Roles.INSURER_MASTER_ADMIN, Roles.INSURER_MASTER_DEPUTY_ADMIN, Roles.INSURER_OTHER_ADMIN]

const GA_LEADER_ROLES = [Roles.GA_LEADER, Roles.GA_BRANCH_MANAGER, Roles.GA_DEPUTY_BRANCH_MANAGER, ...GA_ADMIN_ROLES]
const GA_MANAGER_ROLES = [Roles.GA_MANAGER]
const GA_PLANNER_ROLES = [Roles.GA_PLANNER]
const INS_LEADER_ROLES = [Roles.INSURER_LEADER, Roles.INSURER_BRANCH_MANAGER, Roles.INSURER_DEPUTY_BRANCH_MANAGER, ...INS_ADMIN_ROLES]
const INS_MANAGER_ROLES = [Roles.INSURER_MANAGER, Roles.INSURER_EDUCATION_MANAGER, Roles.INSURER_PLAN_MANAGER]

function RequireAuth({ children }) {
  const { isAuthenticated, initializing } = useAuth()
  const location = useLocation()
  if (initializing) {
    return <div />
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  return children
}

function Shell({ children }) {
  const { user, logout, hasAnyRole } = useAuth()
  const { badgeCount, notifications, markAllRead, dismiss, clear } = useNotifications()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [notifOpen, setNotifOpen] = React.useState(false)
  const [bizSupportOpen, setBizSupportOpen] = React.useState(false)
  const [dataMgmtOpen, setDataMgmtOpen] = React.useState(false)
  const notifRef = React.useRef(null)
  const bizSupportRef = React.useRef(null)
  const dataMgmtRef = React.useRef(null)
  React.useEffect(() => {
    function onOk() {
      // 간단 토스트: 브라우저 기본 alert 대체
      // 실제로는 ToastProvider 사용 권장
      try { window?.toast?.show?.('근무 상태가 저장되었습니다.', 'success') } catch { }
    }
    function onFail() {
      try { window?.toast?.show?.('근무 상태 저장 실패', 'error') } catch { }
    }
    window.addEventListener('availability:changed', onOk)
    window.addEventListener('availability:failed', onFail)
    return () => {
      window.removeEventListener('availability:changed', onOk)
      window.removeEventListener('availability:failed', onFail)
    }
  }, [])

  React.useEffect(() => {
    function onDocDown(e) {
      const t = e?.target
      if (notifOpen) {
        const el = notifRef.current
        if (el && !el.contains(t)) setNotifOpen(false)
      }
      if (bizSupportOpen) {
        const el = bizSupportRef.current
        if (el && !el.contains(t)) setBizSupportOpen(false)
      }
      if (dataMgmtOpen) {
        const el = dataMgmtRef.current
        if (el && !el.contains(t)) setDataMgmtOpen(false)
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') {
        setNotifOpen(false)
        setBizSupportOpen(false)
        setDataMgmtOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocDown)
    window.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      window.removeEventListener('keydown', onEsc)
    }
  }, [notifOpen, bizSupportOpen, dataMgmtOpen])

  // 역할별 메뉴 가시성
  const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const isGaAdmin = hasAnyRole && hasAnyRole(GA_ADMIN_ROLES)
  const isGALeader = hasAnyRole && hasAnyRole(GA_LEADER_ROLES)
  const isGAManager = hasAnyRole && hasAnyRole(GA_MANAGER_ROLES)
  const isGAPlanner = hasAnyRole && hasAnyRole(GA_PLANNER_ROLES)
  const isInsAdmin = hasAnyRole && hasAnyRole(INS_ADMIN_ROLES)
  const isInsLeader = hasAnyRole && hasAnyRole(INS_LEADER_ROLES)
  const isInsManager = hasAnyRole && hasAnyRole(INS_MANAGER_ROLES)
  const isGA = isGaAdmin || isGALeader || isGAManager || isGAPlanner
  const isInsurer = isInsAdmin || isInsLeader || isInsManager

  const showDashboard = true // 전역 허용
  const showOrgs = isOperator || isGaAdmin || isGALeader || isInsAdmin || isInsLeader
  // '사용자/담당자' 메뉴: 우리 조직 사용자(조회/관리) + 파트너 디렉터리(조회)
  const showUsers = isOperator || isGA || isInsurer
  const showRequests = isOperator || isGA || isInsurer
  const showNotices = isOperator || isGA || isInsurer
  // 영업지원(가칭) > 영업이미지(가칭): 기본은 로그인 사용자 전원 노출
  const showSalesImages = true
  // 파일 메뉴는 운영자만 노출 (오픈베타 운영 정책: 일반 사용자는 의뢰/공지의 "첨부"로만 파일을 사용)
  // NOTE: 운영자 전용(파일/변경이력)은 상단 '데이터관리' 대메뉴로 노출한다.
  // - 기존 boolean은 다른 로직에서도 참조될 수 있어, 이 자리에서는 직접 링크 노출만 제거한다.
  // 고객 메뉴: 동의서 출력/발행본 업로드(=GA 실사용)까지 확장
  // - 운영자: 템플릿/좌표 설정 포함
  // - GA: 동의서 출력/업로드만
  // 고객 메뉴는 현재 상단 고정 메뉴에 노출하지 않지만, 권한/라우트 정책에서는 사용될 수 있다.
  // (현재는 메뉴/라우트에서 직접 사용하지 않아 변수로 두지 않는다.)
  // 통계: 관리자(리더/지점장/총무), 설계사, 원수사 리더/매니저 모두 노출
  const showReports = isOperator || isGA || isInsurer
  // 진료정보분석: 기본은 GA 전용이지만, 운영자(관리자)는 전체 접근 허용
  const showHealthAnalysis = isGA || isOperator
  // 화재(건축물대장): 주소 기반 조회 도구 (최소: 로그인 사용자에게 노출)
  const showFireBuildingRegistry = isOperator || isGA || isInsurer
  const showBizSupport = showHealthAnalysis || showFireBuildingRegistry || showSalesImages
  const showSupportInquiries = isOperator // 운영자 전용: 고객문의 처리
  const showDataMgmt = isOperator
  // 디렉터리는 '사용자/담당자' 메뉴 안에서 탭으로 제공

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-50 border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <Link className="flex items-center gap-2" to="/" onClick={() => setMenuOpen(false)}>
            <div className="h-8 w-8 rounded bg-brand-navy" />
            <span className="font-semibold text-lg">설계 EZ</span>
          </Link>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-4 text-sm">
            {showDashboard && <Link className="hover:text-brand-sky" to="/">대시보드</Link>}

            {/* {showUsers && (
              <Link className="hover:text-brand-sky" to="/invites">초대</Link>
            )} */}
            {/* {showCustomers && <Link className="hover:text-brand-sky" to="/customers">고객관리</Link>} */}
            {showRequests && <Link className="hover:text-brand-sky" to="/requests">설계관리</Link>}
            {showNotices && <Link className="hover:text-brand-sky" to="/notices">공지</Link>}
            {showBizSupport && (
              <div ref={bizSupportRef} className="relative">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-brand-sky"
                  aria-haspopup="menu"
                  aria-expanded={bizSupportOpen}
                  onClick={() => setBizSupportOpen(v => !v)}
                >
                  영업지원
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                  </svg>
                </button>
                {bizSupportOpen && (
                  <div className="absolute left-0 top-[36px] w-44 rounded-lg border bg-white shadow-lg py-1">
                    {showHealthAnalysis && (
                      <Link
                        className="block px-3 py-2 text-sm hover:bg-slate-50"
                        to="/health-analysis"
                        onClick={() => setBizSupportOpen(false)}
                      >
                        진료정보분석
                      </Link>
                    )}
                    {showFireBuildingRegistry && (
                      <Link
                        className="block px-3 py-2 text-sm hover:bg-slate-50"
                        to="/fire/building-registry"
                        onClick={() => setBizSupportOpen(false)}
                      >
                        화재보험
                      </Link>
                    )}
                    {showSalesImages && (
                      <Link
                        className="block px-3 py-2 text-sm hover:bg-slate-50"
                        to="/sales-images"
                        onClick={() => setBizSupportOpen(false)}
                      >
                        고객용이미지
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
            {showDataMgmt && (
              <div ref={dataMgmtRef} className="relative">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-brand-sky"
                  aria-haspopup="menu"
                  aria-expanded={dataMgmtOpen}
                  onClick={() => setDataMgmtOpen(v => !v)}
                >
                  데이터관리
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                  </svg>
                </button>
                {dataMgmtOpen && (
                  <div className="absolute left-0 top-[36px] w-52 rounded-lg border bg-white shadow-lg py-1">
                    <Link
                      className="block px-3 py-2 text-sm hover:bg-slate-50"
                      to="/data/uw3nn/exception-rules"
                      onClick={() => setDataMgmtOpen(false)}
                    >
                      예외질환 관리
                    </Link>
                    <div className="my-1 border-t" />
                    <Link
                      className="block px-3 py-2 text-sm hover:bg-slate-50"
                      to="/files"
                      onClick={() => setDataMgmtOpen(false)}
                    >
                      파일
                    </Link>
                    <Link
                      className="block px-3 py-2 text-sm hover:bg-slate-50"
                      to="/audit-logs"
                      onClick={() => setDataMgmtOpen(false)}
                    >
                      변경이력
                    </Link>
                  </div>
                )}
              </div>
            )}
            {showSupportInquiries && <Link className="hover:text-brand-sky" to="/support/inquiries">고객문의</Link>}
            {/* 사용자/담당자 메뉴 안에서 탭으로 제공 */}
            {/* {showSubscriptions && <Link className="hover:text-brand-sky" to="/subscriptions">구독</Link>} */}
            
            {/* 운영자 데이터성 화면은 '데이터관리' 대메뉴로 묶음 */}
            {showReports && <Link className="hover:text-brand-sky" to="/reports">통계</Link>}
            {showOrgs && <Link className="hover:text-brand-sky" to="/orgs">조직관리</Link>}
            {/* {showAvailability && (
              <Link className="hover:text-brand-sky" to="/availability">근무상태</Link>
            )} */}
            {showUsers && (
              <Link className="hover:text-brand-sky" to="/users">사용자/담당자</Link>
            )}
            
            <span className="text-slate-400">|</span>
            <button
              type="button"
              className="relative inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-slate-100 focus:outline-none"
              aria-label="알림"
              onClick={() => {
                setNotifOpen(v => {
                  const next = !v
                  if (next) markAllRead()
                  return next
                })
              }}
            >
              {badgeCount > 0 && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-1 -right-1 inline-flex h-[18px] w-[18px] rounded-full bg-red-400 opacity-60 sgez-notify-ping"
                />
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className={`h-5 w-5 text-slate-600 ${badgeCount > 0 ? 'sgez-notify-shake' : ''}`}
              >
                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.172V11a6 6 0 0 0-12 0v3.172a2 2 0 0 1-.586 1.414L4 17h5" />
                <path d="M9 19a3 3 0 0 0 6 0" />
              </svg>
              {badgeCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] px-1.5 min-w-[18px] h-[18px]">
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div ref={notifRef} className="absolute right-4 top-[64px] w-[360px] max-w-[90vw] rounded-lg border bg-white shadow-lg">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="font-semibold text-sm">알림</div>
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => clear()}>
                      모두 지우기
                    </button>
                    <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setNotifOpen(false)}>
                      닫기
                    </button>
                  </div>
                </div>
                <div className="max-h-80 overflow-auto">
                  {(Array.isArray(notifications) ? notifications : []).slice(0, 15).map((n) => {
                    const p = n?.payload || {}
                    const title = p?.title || '알림'
                    const summary = p?.summary || p?.message || p?.insurerOrgName || ''
                    const when = n?.receivedAt ? String(n.receivedAt).replace('T', ' ').replace('Z', '') : ''
                    const reqId = p?.requestId
                    const noticeId = p?.noticeId
                    const inquiryId = p?.inquiryId
                    const href = noticeId ? `/notices/${noticeId}` : (reqId ? `/requests/${reqId}` : (inquiryId ? `/support/inquiries/${inquiryId}` : null))
                    return (
                      <div key={n.id} className="px-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="text-left flex-1"
                            onClick={() => {
                              if (href) navigate(href)
                              else navigate('/dashboard')
                              setNotifOpen(false)
                            }}
                          >
                            <div className="text-sm font-medium text-slate-800">{title}</div>
                            {summary && <div className="text-xs text-slate-600 mt-0.5">{summary}</div>}
                            <div className="text-[11px] text-slate-400 mt-1">{when}</div>
                          </button>
                          <button type="button" className="text-xs text-slate-400 hover:text-slate-700" onClick={() => dismiss(n.id)}>
                            삭제
                          </button>
                        </div>
                      </div>
                    )
                  })}
                  {(Array.isArray(notifications) ? notifications : []).length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-slate-500">알림이 없습니다</div>
                  )}
                </div>
                <div className="px-3 py-2 border-t text-xs text-slate-500">
                  알림을 클릭하면 관련 화면으로 이동합니다. (의뢰/공지 중심)
                </div>
              </div>
            )}
            <span className="text-slate-600">{user?.name}</span>
            <Link className="hover:text-brand-sky" to="/profile">내 정보</Link>
            <button className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => { logout(); navigate('/login') }}>로그아웃</button>
          </nav>
          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded border text-slate-700 hover:bg-slate-50"
            aria-label="메뉴"
            aria-controls="mobile-nav"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(v => !v)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
              <path fillRule="evenodd" d="M3.75 6.75a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        {/* Mobile nav drawer */}
        {menuOpen && (
          <div id="mobile-nav" className="md:hidden border-t">
            <div className="mx-auto max-w-6xl px-4 py-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-slate-700 text-sm">{user?.name}</span>
                <Link className="text-sm hover:text-brand-sky" to="/profile" onClick={() => setMenuOpen(false)}>내 정보</Link>
              </div>
              <nav className="flex flex-col gap-3 text-sm py-2">
                {showDashboard && <Link className="hover:text-brand-sky" to="/" onClick={() => setMenuOpen(false)}>대시보드</Link>}
                {/* {showCustomers && <Link className="hover:text-brand-sky" to="/customers" onClick={() => setMenuOpen(false)}>고객관리</Link>} */}
                {showBizSupport && (
                  <div className="pt-2">
                    <div className="text-xs font-semibold text-slate-500">영업지원</div>
                    <div className="mt-2 flex flex-col gap-2 pl-3 border-l">
                      {showHealthAnalysis && (
                        <Link className="hover:text-brand-sky" to="/health-analysis" onClick={() => setMenuOpen(false)}>
                          진료정보분석
                        </Link>
                      )}
                      {showFireBuildingRegistry && (
                        <Link className="hover:text-brand-sky" to="/fire/building-registry" onClick={() => setMenuOpen(false)}>
                          화재보험
                        </Link>
                      )}
                      {showSalesImages && (
                        <Link className="hover:text-brand-sky" to="/sales-images" onClick={() => setMenuOpen(false)}>
                          고객용이미지
                        </Link>
                      )}
                    </div>
                  </div>
                )}
                {showRequests && <Link className="hover:text-brand-sky" to="/requests" onClick={() => setMenuOpen(false)}>설계관리</Link>}
                {showNotices && <Link className="hover:text-brand-sky" to="/notices" onClick={() => setMenuOpen(false)}>공지</Link>}
                {showDataMgmt && (
                  <div className="pt-2">
                    <div className="text-xs font-semibold text-slate-500">데이터관리</div>
                    <div className="mt-2 flex flex-col gap-2 pl-3 border-l">
                      <Link className="hover:text-brand-sky" to="/data/uw3nn/exception-rules" onClick={() => setMenuOpen(false)}>
                        예외질환 관리
                      </Link>
                      <Link className="hover:text-brand-sky" to="/files" onClick={() => setMenuOpen(false)}>
                        파일
                      </Link>
                      <Link className="hover:text-brand-sky" to="/audit-logs" onClick={() => setMenuOpen(false)}>
                        변경이력
                      </Link>
                    </div>
                  </div>
                )}
                {showSupportInquiries && <Link className="hover:text-brand-sky" to="/support/inquiries" onClick={() => setMenuOpen(false)}>고객문의</Link>}
                {/* 사용자/담당자 메뉴 안에서 탭으로 제공 */}
                {/* {showSubscriptions && <Link className="hover:text-brand-sky" to="/subscriptions" onClick={() => setMenuOpen(false)}>구독</Link>} */}
                {/* 운영자 데이터성 화면은 '데이터관리' 섹션에 표시 */}
                {showReports && <Link className="hover:text-brand-sky" to="/reports" onClick={() => setMenuOpen(false)}>통계</Link>}
                {showOrgs && <Link className="hover:text-brand-sky" to="/orgs" onClick={() => setMenuOpen(false)}>조직관리</Link>}
                {showUsers && (
                  <Link className="hover:text-brand-sky" to="/users" onClick={() => setMenuOpen(false)}>사용자/담당자</Link>
                )}
                <button
                  className="mt-2 rounded border px-3 py-2 text-left hover:bg-slate-50"
                  onClick={() => { setMenuOpen(false); logout(); navigate('/login') }}
                >
                  로그아웃
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
            <div className="space-y-2">
              <div className="font-semibold text-slate-800">설계 EZ</div>
              <div className="text-xs text-slate-500 leading-relaxed">
                설계 EZ (참좋은가족건강연구소) · 대표: 황인태 <br />
                사업자등록번호: 586-18-01473 · 주소: 인천광역시 서구 청라에메랄드로 134, 211동 1202호<br />
                {/* 고객센터:  · 이메일: */}
              </div>
              <div className="text-xs text-slate-400">© {new Date().getFullYear()} 설계 EZ. All rights reserved.</div>
            </div>

            <div className="space-y-2">
              <div className="font-semibold text-slate-800">고객지원</div>
              <div className="flex flex-col gap-2 text-sm">
                {!isOperator && (
                  <>
                    <Link className="text-slate-600 hover:text-brand-sky" to="/support/inquiries">문의하기(고객문의)</Link>
                    <span className="text-xs text-slate-500">문의/답변은 비공개이며, 로그인 후 이용 가능합니다.</span>
                  </>
                )}
                {isOperator && (
                  <span className="text-xs text-slate-500">고객문의는 상단 메뉴에서 확인할 수 있습니다.</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-semibold text-slate-800">정책</div>
              <div className="flex flex-col gap-2 text-sm">
                <Link className="text-slate-600 hover:text-brand-sky" to="/terms">이용약관</Link>
                <Link className="text-slate-600 hover:text-brand-sky" to="/privacy">개인정보처리방침</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default function App() {
  // 공지 외부발송(카카오/SMS) 관련 화면(템플릿/발송로그)은 현재 숨김.
  // 필요 시 `.env`에 REACT_APP_ENABLE_NOTICE_EXTERNAL_SEND=true 설정으로 노출.
  const enableNoticeExternalSend = String(process.env.REACT_APP_ENABLE_NOTICE_EXTERNAL_SEND || '').toLowerCase() === 'true'
  // 설계의뢰 공유링크(비로그인 열람) UI는 기본 비활성화
  // 필요 시 `.env`에 REACT_APP_ENABLE_REQUEST_SHARE_LINK=true 설정으로 노출.
  const enableRequestShareLink = String(process.env.REACT_APP_ENABLE_REQUEST_SHARE_LINK || '').toLowerCase() === 'true'
  const queryClient = new QueryClient()
  return (
    <BrowserRouter>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <NotificationProvider>
              <ErrorBoundary>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  {/* 비로그인 공개 공유 링크(기본 숨김) */}
                  {enableRequestShareLink && (
                    <Route path="/share/request/:token" element={<RequestSharePage />} />
                  )}
                  {/* <Route path="/invites/accept/:code" element={<InviteAcceptPage />} /> */}
                  {/* <Route path="/invites" element={<RequireAuth><Shell><RequireRole roles={[Roles.OPERATOR, Roles.GA_MANAGER, Roles.INSURER_MANAGER]} fallback={<ForbiddenPage />}><InvitesPage /></RequireRole></Shell></RequireAuth>} /> */}
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <Shell>
                          <DashboardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/orgs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[...GA_LEADER_ROLES, ...INS_LEADER_ROLES, Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OrgsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <RequireAuth>
                        <Shell>
                          <UsersPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/signups"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole
                            roles={[
                              Roles.OPERATOR,
                              ...GA_LEADER_ROLES,
                              ...INS_LEADER_ROLES
                            ]}
                            fallback={<ForbiddenPage />}
                          >
                            <SignupRequestsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/signups/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole
                            roles={[
                              Roles.OPERATOR,
                              ...GA_LEADER_ROLES,
                              ...INS_LEADER_ROLES
                            ]}
                            fallback={<ForbiddenPage />}
                          >
                            <SignupRequestDetailPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/availability"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[...GA_LEADER_ROLES, ...INS_LEADER_ROLES, Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <AvailabilityAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/users/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <UserDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <Shell>
                          <ProfilePage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/fire/building-registry"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...GA_PLANNER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <FireBuildingRegistryPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_PLANNER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <RequestsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_PLANNER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <RequestNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_PLANNER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <RequestDetailPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices"
                    element={
                      <RequireAuth>
                        <Shell>
                          <NoticesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <NoticeDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <NoticeEditPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/templates"
                    element={
                      <RequireAuth>
                        <Shell>
                          {enableNoticeExternalSend ? <NoticeTemplatesPage /> : <NotFoundPage />}
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          {enableNoticeExternalSend ? <NoticeLogsPage /> : <NotFoundPage />}
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <NoticeNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/counterparts"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CounterpartDirectoryPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesListPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesCreatePage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesEditPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/uw3nn/exception-rules"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Uw3nnExceptionRulesAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/files"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <FilesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/audit-logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <AuditLogsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/health-analysis"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...GA_PLANNER_ROLES]} fallback={<ForbiddenPage />}>
                            <HealthAnalysisPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...GA_PLANNER_ROLES, ...INS_LEADER_ROLES, ...INS_MANAGER_ROLES]} fallback={<ForbiddenPage />}>
                            <ReportsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR, ...GA_LEADER_ROLES, ...GA_MANAGER_ROLES, ...GA_PLANNER_ROLES]} fallback={<ForbiddenPage />}>
                            <CustomersPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/inquiries"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SupportInquiriesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/inquiries/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SupportInquiryDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ErrorBoundary>
            </NotificationProvider>
          </ToastProvider>
        </QueryClientProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
